import { FilterOutlined, RightOutlined } from '@ant-design/icons';
import {
  Col,
  Dropdown,
  Row,
  Checkbox,
  Divider,
  Typography,
  Collapse,
} from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import {
  BloodType,
  DonorLastDonationIntervalInput,
  DonorListFilterInput,
  DonorStatus,
  DonorType,
} from 'gql/types';
import { Button } from 'modules/common/components';
import { LocationsPickerDrawer } from 'modules/common/components/LocationsPickerDrawer';
import { UseFilterObject } from 'modules/common/hooks/useFilter';
import { DonationDateFilters } from 'modules/donor/types';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getOptions } from './options';
import styles from './styles.module.scss';

type PropTypes = {
  onFilter?: (value?: DonorListFilterInput) => void;
  defaultValues?: DonorListFilterInput;
  donorListFilters?: UseFilterObject<DonorListFilterInput>;
};

const IndividualDonorListFilter: FC<PropTypes> = ({
  defaultValues,
  donorListFilters,
}) => {
  const { t } = useTranslation('donor.IndividualDonorListFilter');

  const [isVisible, setIsVisible] = useState(false);
  const [isLocationsDrawerVisible, setIsLocationsDrawerVisible] =
    useState(false);
  const [filter, setFilter] =
    useState<Record<keyof DonorListFilterInput, CheckboxValueType[]>>();

  const selectedLocations = useMemo(() => {
    return (filter?.locations as string[]) || [];
  }, [filter?.locations]);

  const locationsDisplayText = useMemo(() => {
    if (selectedLocations.length === 0) {
      return t('allLocations');
    }
    if (selectedLocations.length <= 2) {
      return selectedLocations.join(', ');
    }
    return `${selectedLocations.slice(0, 2).join(', ')} +${
      selectedLocations.length - 2
    } ${t('more')}`;
  }, [selectedLocations, t]);

  const toFilter = (
    filters: Record<keyof DonorListFilterInput, CheckboxValueType[]>
  ): DonorListFilterInput => {
    const statuses = filters.statuses as DonorStatus[] | undefined;
    const types = filters.types as DonorType[] | undefined;
    const bloodType = filters.bloodType as BloodType[] | undefined;
    const donationDates = filters.lastDonationInterval as
      | DonationDateFilters[]
      | undefined;
    const locations = filters.locations as string[] | undefined;

    const donationIntervals = donationDates?.reduce<{
      from: number[];
      to: number[];
    }>(
      (interval, donationDate) => {
        const { from, to } = donationDateIntervals[donationDate];
        return {
          from: [...interval.from, from],
          to: [...interval.to, to],
        };
      },
      { from: [], to: [] }
    );
    const lastDonationInterval =
      donationDates?.length && donationIntervals
        ? {
            from: Math.min(...donationIntervals.from),
            to: Math.max(...donationIntervals.to),
          }
        : undefined;
    return {
      statuses,
      types,
      bloodType,
      lastDonationInterval,
      locations,
    };
  };

  const setIsVisibleTo = (value: boolean) => () => setIsVisible(value);

  const handleVisibleChange = (flag: boolean) => {
    if (!isLocationsDrawerVisible) {
      setIsVisible(flag);
    }
  };

  const filterHandler = () => {
    const transformedFilterValues = filter ? toFilter(filter) : filter;
    transformedFilterValues &&
      Object.keys(transformedFilterValues).forEach((fieldName) => {
        donorListFilters?.setFilterOption({
          fieldName: fieldName as keyof DonorListFilterInput,
          value: (transformedFilterValues as any)[fieldName],
        });
      });
    setIsVisible(false);
  };

  const onCheckboxChange =
    (key: keyof DonorListFilterInput) => (value: CheckboxValueType[]) => {
      setFilter({ ...filter, [key]: value } as any);
    };

  const handleLocationsApply = (locations: string[]) => {
    setFilter({ ...filter, locations } as any);
  };

  useEffect(() => {
    if (defaultValues) {
      const mappedValues = Object.keys(defaultValues).reduce(
        (filterValues, key) => {
          const filterFieldName = key as keyof DonorListFilterInput;
          if (key === 'lastDonationInterval') {
            const donationDates = defaultValues[
              filterFieldName
            ] as DonorLastDonationIntervalInput;

            const lastDonationInterval = Object.keys(
              donationDateIntervals
            ).filter((key) => {
              const { to, from } =
                donationDateIntervals[key as DonationDateFilters];
              const isSomeIntervalEdgeNotExist =
                typeof donationDates?.from !== 'number' ||
                typeof donationDates?.to !== 'number';

              if (isSomeIntervalEdgeNotExist) {
                return false;
              }

              const toValue = donationDates?.to as number;
              const fromValue = donationDates?.from as number;
              return to <= toValue && from >= fromValue;
            });

            return { ...filterValues, lastDonationInterval };
          }
          return { ...filterValues, [key]: defaultValues[filterFieldName] };
        },
        {} as Record<keyof DonorListFilterInput, CheckboxValueType[]>
      );
      setFilter(mappedValues);
    }
  }, [defaultValues, isVisible]);

  const options = getOptions({ locations: [] }).filter(
    (option) => option.key !== 'locations'
  );

  const collapseItems = options.map(({ title, key, values }) => {
    const filterValue = filter?.[key] as CheckboxValueType[] | undefined;
    const selectedCount = filterValue?.length || 0;

    return {
      key,
      label: (
        <div className={styles.sectionLabel}>
          <span className={styles.sectionTitle}>{title}</span>
          {selectedCount > 0 && (
            <Typography.Text type="secondary" className={styles.selectedCount}>
              ({selectedCount})
            </Typography.Text>
          )}
        </div>
      ),
      children: (
        <Checkbox.Group
          value={filterValue}
          onChange={onCheckboxChange(key)}
          className={styles.filtersGroup}
        >
          <Row gutter={[0, 8]}>
            {values.map(({ value, label }, i) => (
              <Col span={24} key={i}>
                <Checkbox value={value}>
                  <span className={styles.filterLabel}>{label}</span>
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      ),
    };
  });

  return (
    <>
      <Dropdown
        onOpenChange={handleVisibleChange}
        open={isVisible}
        overlayClassName={styles.dropdownOverlay}
        trigger={['click']}
        dropdownRender={() => (
          <div className={styles.dropdownContent}>
            <div className={styles.filtersWrapper}>
              <Collapse
                defaultActiveKey={options.map((o) => String(o.key))}
                ghost
                expandIconPosition="end"
                className={styles.collapse}
              >
                {collapseItems.map(({ key, label, children }) => (
                  <Collapse.Panel key={String(key)} header={label}>
                    {children}
                  </Collapse.Panel>
                ))}
              </Collapse>

              <div
                className={styles.locationsRow}
                onClick={() => setIsLocationsDrawerVisible(true)}
              >
                <Row justify="space-between" align="middle">
                  <div>
                    <Typography.Text strong className={styles.locationsTitle}>
                      {t('locations')}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className={styles.locationsValue}
                    >
                      {locationsDisplayText}
                    </Typography.Text>
                  </div>
                  <RightOutlined className={styles.arrowIcon} />
                </Row>
              </div>
            </div>

            <Divider className={styles.divider} />

            <div className={styles.buttonWrapper}>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Button size="large" onClick={setIsVisibleTo(false)} block>
                    {t('cancel')}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    size="large"
                    type="primary"
                    onClick={filterHandler}
                    block
                  >
                    {t('filter')}
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        )}
      >
        <FilterOutlined
          className={styles.filterIcon}
          onClick={setIsVisibleTo(true)}
        />
      </Dropdown>

      <LocationsPickerDrawer
        visible={isLocationsDrawerVisible}
        onClose={() => setIsLocationsDrawerVisible(false)}
        selectedLocations={selectedLocations}
        onApply={handleLocationsApply}
      />
    </>
  );
};

const donationDateIntervals: Record<
  DonationDateFilters,
  DonorLastDonationIntervalInput
> = {
  [DonationDateFilters.from0to24daysFilter]: {
    from: 0,
    to: 24,
  },
  [DonationDateFilters.from24to29daysFilter]: {
    from: 24,
    to: 29,
  },
  [DonationDateFilters.moreThan30daysFilter]: {
    from: 30,
    to: 365,
  },
};

export default IndividualDonorListFilter;
